import { useEffect, useRef } from 'react'

import { useConstant } from '../useConstant'

import { NO_PENDING } from './useThrottledState.constants'
import { isThrottledStatePending } from './useThrottledState.utils'

/**
 * The {@link useThrottledState} schedules an async `commit` function to run
 * with a throttled value, while serializing in-flight commits.
 *
 * @usage
 * `const [schedule, getLatest] = useThrottledState(commit)`
 *
 * - `schedule(value, timeout)` arms a one-shot timer; subsequent calls
 *   during the throttle window overwrite the buffered value but do NOT
 *   reschedule.
 * - `schedule(value, 0)` flushes immediately, cancelling any pending
 *   throttle timer first.
 * - If a commit is already in flight when a new value is scheduled, the new
 *   value is queued. Once the running commit settles, one more commit runs
 *   with the latest queued value. Multiple changes during a commit collapse
 *   into a single follow-up.
 * - Errors thrown by `commit` are caught so a newer queued value still gets
 *   a chance to run.
 *
 * `getLatest()` returns the most recently scheduled value (regardless of
 * whether it has already been committed or is still pending). Useful when
 * the consumer needs to compute the next value relative to the latest
 * known one before calling `schedule`.
 *
 * `cancel()` drops any pending (not-yet-running) value and clears the
 * throttle timer. A commit that is already in flight cannot be aborted and
 * will still settle with whatever value it picked up before `cancel`.
 *
 * All returned functions are stable for the lifetime of the component.
 *
 * @template TValue
 * @param {(value: TValue) => Promise<unknown>} commitCallback
 * @returns {[
 *   (value: TValue, timeout?: number) => void,
 *   () => TValue | undefined,
 *   () => void,
 * ]}
 */
export function useThrottledState(commitCallback) {
  const commitCallbackRef = useRef(commitCallback)

  useEffect(() => {
    commitCallbackRef.current = commitCallback
  }, [commitCallback])

  const latestRef = useRef(undefined)
  const pendingRef = useRef(NO_PENDING)
  const timerRef = useRef(null)
  const flushingRef = useRef(false)

  const flush = useConstant(
    () =>
      async function flush() {
        if (!flushingRef.current) {
          flushingRef.current = true

          try {
            while (isThrottledStatePending(pendingRef.current)) {
              const value = pendingRef.current

              pendingRef.current = NO_PENDING

              try {
                await commitCallbackRef.current(value)
              } catch {
                // Swallow so newer queued values still get a chance to commitCallback.
              }
            }
          } finally {
            flushingRef.current = false
          }
        }
      }
  )

  const schedule = useConstant(
    () =>
      function schedule(value, timeout = 0) {
        latestRef.current = value
        pendingRef.current = value

        if (timeout === 0) {
          if (timerRef.current !== null) {
            clearTimeout(timerRef.current)
            timerRef.current = null
          }

          flush()

          return
        }

        if (timerRef.current === null) {
          timerRef.current = setTimeout(() => {
            timerRef.current = null
            flush()
          }, timeout)
        }
      }
  )

  const getLatest = useConstant(
    () =>
      function getLatest() {
        return latestRef.current
      }
  )

  const cancel = useConstant(
    () =>
      function cancel() {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }

        pendingRef.current = NO_PENDING
        latestRef.current = undefined
      }
  )

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }
    },
    []
  )

  return [schedule, getLatest, cancel]
}
